/**
 * Splash "seen" persistence.
 *
 * The auto-show splash fires only ONCE on a visitor's first visit and never
 * again — across tabs, browser restarts, and return visits. The flag lives
 * in localStorage rather than sessionStorage (which was per-browsing-session)
 * so it actually persists. The "About" menu still shows the splash on demand
 * because manual shows never read or write this flag.
 *
 * Why localStorage and not a cookie: a JS-set cookie (document.cookie) is
 * capped to 7 days under Safari/iOS Intelligent Tracking Prevention, so a
 * "long-term" cookie would quietly re-show the splash every week on Apple
 * devices. First-party localStorage isn't subject to that cap, so it's the
 * more durable choice for "show once, ever." Every access is wrapped: if
 * storage is blocked entirely (locked-down privacy modes), the splash simply
 * shows that visit instead of throwing.
 */

const APEX_SEEN_KEY = 'trusscore_splash_seen';

/**
 * Per-retailer seen-key — each partner brand gets its own one-time impression
 * budget. Falls back to the apex key when no partner is active.
 */
export function seenStorageKey(partnerId: number | null | undefined): string {
  return partnerId ? `${APEX_SEEN_KEY}:partner:${partnerId}` : APEX_SEEN_KEY;
}

/** Has this visitor already seen the (apex or partner) splash? */
export function hasSeenSplash(partnerId: number | null | undefined): boolean {
  try {
    return localStorage.getItem(seenStorageKey(partnerId)) === '1';
  } catch {
    return false; // storage blocked → treat as unseen, show this visit
  }
}

/** Remember that this visitor has seen the (apex or partner) splash. */
export function markSplashSeen(partnerId: number | null | undefined): void {
  try {
    localStorage.setItem(seenStorageKey(partnerId), '1');
  } catch {
    /* storage blocked — nothing to persist */
  }
}

/**
 * Dev helper: forget every splash-seen flag (apex + all partners) so the
 * splash auto-shows again on next load. Used by the dev tools "Clear Session"
 * button, which previously relied on sessionStorage.clear().
 */
export function clearSplashSeen(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(APEX_SEEN_KEY)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
